import React, { useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch, STATUS_SWITCH, STATUS_SWITCH_INVERTED } from "@/components/ui/switch";
import { Slider } from "@/components/ui/slider";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/components/ui/tabs";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { cn } from "@/lib/utils";
import { US_STATES, CA_PROVINCES, COUNTRIES } from "@/utils/locationData";
import { getStorePreviewUrl } from "@/utils/storeUtils";
import { getImageUrl } from "@/utils/imageUtils";
import { MODULES, MODULE_LABELS, MODULE_BADGE, resolveModuleStates } from "@/utils/storeModules";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  Store,
  Search,
  RefreshCw,
  Eye,
  Pencil,
  DollarSign,
  Palette,
  User,
  Save,
  Upload,
  Loader2,
  X,
  Building,
  Mail,
  Phone,
  FileText,
  Lock,
  EyeOff,
  ChevronsUpDown,
  Check,
  Facebook,
  Instagram,
  Twitter,
  Linkedin,
  Globe
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const ALL = "all"; // sentinel for "no filter" (Radix Select disallows an empty value)
const SITE_URL = (process.env.REACT_APP_FRONTEND_URL || window.location.origin).replace(/\/$/, '');

const formatDate = (value) => {
  if (!value) return '';
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? '' : d.toLocaleDateString();
};

export default function AdminResellers() {
  const [stores, setStores] = useState([]);
  const [themePresets, setThemePresets] = useState({});
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  // Radix Select disallows an empty value, so "all" is the no-filter sentinel.
  const [filterModule, setFilterModule] = useState(ALL);
  const [filterStatus, setFilterStatus] = useState(ALL);

  // Pagination state
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(15);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);

  // Edit panel state
  const [selectedStore, setSelectedStore] = useState(null);
  const [panelOpen, setPanelOpen] = useState(false);
  const [activeTab, setActiveTab] = useState("info");
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [blockDialogOpen, setBlockDialogOpen] = useState(false);
  const [storeToToggle, setStoreToToggle] = useState(null);
  const [savingModules, setSavingModules] = useState(false);
  const fileInputRef = useRef(null);
  const [distributors, setDistributors] = useState([]);
  const [stateDropdownOpen, setStateDropdownOpen] = useState(false);

  // Form data for editing
  const [formData, setFormData] = useState({
    // Business Info
    business_name: "",
    subdomain: "",
    logo_url: "",
    contact_email: "",
    contact_phone: "",
    contact_name: "",
    address: "",
    business_address: {
      address: "",
      city: "",
      state: "",
      zipcode: "",
      country: "US"
    },
    social_links: {
      facebook: "",
      instagram: "",
      twitter: "",
      linkedin: "",
      website: ""
    },
    business_website: "",
    business_description: "",
    distributor_id: "",
    // Pricing & Settings
    global_price_multiplier: 2.5,
    max_price_multiplier: 3.0,
    min_order_amount: 0,
    commission_type: "markup",
    commission_percentage: 0,
    stripe_account_id: "",
    payout_threshold: 100,
    auto_payout_enabled: false,
    // Theme
    theme_preset: "classic_elegant",
    custom_primary_color: "",
    custom_accent_color: "",
    custom_secondary_color: "",
    custom_background_color: "",
    custom_text_color: "",
    custom_font_family: "",
    custom_header_bg_color: "",
    // Account
    user_name: "",
    user_email: "",
    user_password: "",
    show_business_name: true
  });

  const [userData, setUserData] = useState(null);

  const getAuthHeaders = () => ({
    headers: { Authorization: `Bearer ${localStorage.getItem("session_token")}` }
  });

  const fetchData = useCallback(async () => {
    try {
      const authHeaders = getAuthHeaders();
      const [storesRes, presetsRes, distRes] = await Promise.all([
        axios.get(`${API}/stores`, {
          ...authHeaders,
          params: {
            page,
            limit,
            keyword: searchTerm,
            // Pagination is server-side, so the filters have to be too — narrowing
            // the current page in the browser would report the wrong totals.
            ...(filterModule !== ALL && { module: filterModule }),
            ...(filterStatus !== ALL && { status: filterStatus })
          }
        }),
        axios.get(`${API}/theme-presets`, authHeaders),
        axios.get(`${API}/admin/distributors-list`, authHeaders)
      ]);
      
      if (storesRes.data.data && storesRes.data.pagination) {
        setStores(storesRes.data.data);
        setTotalPages(storesRes.data.pagination.totalPages);
        setTotalItems(storesRes.data.pagination.total);
      } else {
        setStores(storesRes.data);
        setTotalPages(1);
        setTotalItems(storesRes.data.length);
      }
    setThemePresets(presetsRes.data);
    setDistributors(distRes.data || []);
    } catch (error) {
      toast.error("Failed to fetch data");
    } finally {
      setLoading(false);
    }
  }, [page, limit, searchTerm, filterModule, filterStatus]);

  useEffect(() => {
    fetchData();
  }, [page, limit, searchTerm, fetchData]);

  const openEditPanel = async (store) => {
    setSelectedStore(store);
    const bi = store.business_info || {};
    const rs = store.reseller_settings || {};
    const theme = store.theme || {};
    const ba = store.business_address || {};
    const sl = store.social_links || {};

    // Contact info can be in business_info or directly on store
    const contactEmail = bi.contact_email || store.contact_email || "";
    const contactPhone = bi.contact_phone || store.contact_phone || "";
    const logoUrl = bi.logo_url || store.logo_url || "";
    const address = ba.address || bi.address || store.address || "";
    const website = store.business_website || sl.website || "";
    const description = bi.business_description || store.business_description || "";

    setFormData({
      // Business Info
      business_name: store.business_name || "",
      subdomain: store.subdomain || "",
      logo_url: logoUrl,
      contact_email: contactEmail,
      contact_phone: contactPhone,
      contact_name: store.contact_name || bi.contact_name || "",
      address: address,
      business_address: {
        address: address,
        city: ba.city || bi.city || "",
        state: ba.state || bi.state || "",
        zipcode: ba.zipcode || bi.zipcode || "",
        country: ba.country || bi.country || "US"
      },
      social_links: {
        facebook: sl.facebook || "",
        instagram: sl.instagram || "",
        twitter: sl.twitter || "",
        linkedin: sl.linkedin || "",
        website: website
      },
      business_website: website,
      business_description: description,
      distributor_id: store.distributor_id || "",
      // Pricing & Settings
      global_price_multiplier: store.global_price_multiplier || 2.5,
      max_price_multiplier: rs.max_price_multiplier || 3.0,
      min_order_amount: rs.min_order_amount || 0,
      commission_type: rs.commission_type || "markup",
      commission_percentage: rs.commission_percentage || 0,
      stripe_account_id: rs.stripe_account_id || "",
      payout_threshold: rs.payout_threshold || 100,
      auto_payout_enabled: rs.auto_payout_enabled || false,
      is_active: store.is_active !== false,
      // Theme
      theme_preset: theme.preset || "classic_elegant",
      custom_primary_color: theme.custom_primary_color || "",
      custom_accent_color: theme.custom_accent_color || "",
      custom_secondary_color: theme.custom_secondary_color || "",
      custom_background_color: theme.custom_background_color || "",
      custom_text_color: theme.custom_text_color || "",
      custom_font_family: theme.custom_font_family || "",
      custom_header_bg_color: theme.custom_header_bg_color || "",
      // Account (loaded separately)
      user_name: "",
      user_email: "",
      user_password: "",
      show_business_name: store.show_business_name ?? true
    });

    // Load user data
    if (store.user_id) {
      try {
        const userRes = await axios.get(`${API}/admin/users/${store.user_id}`, getAuthHeaders());
        setUserData(userRes.data);
        setFormData(prev => ({
          ...prev,
          user_name: userRes.data.name || "",
          user_email: userRes.data.email || "",
          user_password: userRes.data.password || ""
        }));
      } catch (error) {
        console.error("Failed to load user data");
        // Set userData to empty object to show "No account" state
        setUserData({ noAccount: true });
      }
    } else {
      // No user_id means no account linked to this store
      setUserData({ noAccount: true });
    }

    setActiveTab("info");
    setPanelOpen(true);
  };

  const closePanel = () => {
    setPanelOpen(false);
    setSelectedStore(null);
    setUserData(null);
    setShowPassword(false);
  };

  const handleLogoUpload = async (file) => {
    if (!file) return;

    const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Invalid file type. Please upload JPG, PNG, GIF, or WebP.');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error('File too large. Maximum size is 5MB.');
      return;
    }

    setUploading(true);
    try {
      const uploadData = new FormData();
      uploadData.append('logo', file);
      const response = await axios.post(`${API}/upload/logo`, uploadData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      const logoUrl = response.data.url;
      setFormData(prev => ({ ...prev, logo_url: logoUrl }));
      toast.success('Logo uploaded!');
    } catch (error) {
      const msg = error.response?.data?.message || error.response?.data?.detail || 'Failed to upload logo';
      toast.error(msg);
    } finally {
      setUploading(false);
    }
  };

  const saveInfo = async () => {
    if (!selectedStore) return;
    setSaving(true);
    try {
      // Use the correct endpoint: /admin/stores/{store_id}
      await axios.put(`${API}/admin/stores/${selectedStore.store_id}`, {
        business_name: formData.business_name,
        subdomain: formData.subdomain,
        logo_url: formData.logo_url,
        contact_email: formData.contact_email,
        contact_phone: formData.contact_phone,
        contact_name: formData.contact_name,
        business_address: formData.business_address,
        social_links: formData.social_links,
        business_website: formData.business_website,
        business_description: formData.business_description,
        distributor_id: formData.distributor_id || null,
        show_business_name: formData.show_business_name
      }, getAuthHeaders());
      toast.success("Business info saved!");
      fetchData();
    } catch (error) {
      const msg = error.response?.data?.message || error.response?.data?.detail || "Failed to save";
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  };

  const savePricing = async () => {
    if (!selectedStore) return;
    setSaving(true);
    try {      const pricingData = {
        global_price_multiplier: formData.global_price_multiplier,
        reseller_settings: {
          max_price_multiplier: formData.max_price_multiplier,
          min_order_amount: formData.min_order_amount,
          commission_type: formData.commission_type,
          commission_percentage: formData.commission_percentage,
          stripe_account_id: formData.stripe_account_id,
          payout_threshold: formData.payout_threshold,
          auto_payout_enabled: formData.auto_payout_enabled
        }
      };
      await axios.put(`${API}/admin/reseller-settings/${selectedStore.store_id}`, pricingData, getAuthHeaders());
      toast.success("Pricing settings saved!");
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  const saveTheme = async () => {
    if (!selectedStore) return;
    setSaving(true);
    try {
      const themeData = {
        preset: formData.theme_preset,
        custom_primary_color: formData.custom_primary_color,
        custom_accent_color: formData.custom_accent_color,
        custom_secondary_color: formData.custom_secondary_color,
        custom_background_color: formData.custom_background_color,
        custom_text_color: formData.custom_text_color,
        custom_font_family: formData.custom_font_family,
        custom_header_bg_color: formData.custom_header_bg_color
      };
      await axios.put(`${API}/admin/store-theme/${selectedStore.store_id}`, themeData, getAuthHeaders());
      toast.success("Theme saved!");
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  const toggleBlock = (store) => {
    setStoreToToggle(store);
    setBlockDialogOpen(true);
  };

  const confirmToggleBlock = async () => {
    if (!storeToToggle) return;
    const action = storeToToggle.is_active ? 'block' : 'unblock';
    try {
      await axios.put(`${API}/admin/reseller-settings/${storeToToggle.store_id}`, {
        is_active: !storeToToggle.is_active
      }, getAuthHeaders());
      toast.success(`Store ${action}ed successfully!`);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || `Failed to ${action} store`);
    } finally {
      setBlockDialogOpen(false);
      setStoreToToggle(null);
    }
  };

  // Platform enablement goes through its own endpoint rather than the general
  // store update, so ever_enabled and the toggle audit trail survive.
  const toggleModule = async (moduleName, enabled) => {
    if (!selectedStore) return;
    setSavingModules(true);
    try {
      const res = await axios.put(
        `${API}/admin/stores/${selectedStore.store_id}/modules`,
        { [moduleName]: enabled },
        getAuthHeaders()
      );
      // Keep the open panel in step without a full refetch.
      setSelectedStore(prev => (prev ? { ...prev, modules: res.data.modules } : prev));
      toast.success(`${MODULE_LABELS[moduleName]} turned ${enabled ? 'on' : 'off'}.`);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to update platforms');
    } finally {
      setSavingModules(false);
    }
  };

  const saveAccount = async () => {
    if (!selectedStore || !userData) return;
    setSaving(true);
    try {
      await axios.put(`${API}/admin/users/${selectedStore.user_id}`, {
        name: formData.user_name,
        email: formData.user_email,
        password: formData.user_password
      }, getAuthHeaders());
      toast.success("Account updated!");
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save");
    } finally {
      setSaving(false);
    }
  };


  const filteredStores = stores;
  // Module/terms state for the store open in the edit panel.
  const selectedModuleStates = resolveModuleStates(selectedStore);
  const selectedTerms = selectedStore?.terms_acceptance || {};

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 text-primary animate-spin" />
      </div>
    );
  }

  return (
    // Nothing else in the app mounts a TooltipProvider, so this page brings its own.
    <TooltipProvider delayDuration={200}>
    <div className="space-y-6" data-testid="admin-resellers">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-3">
            Reseller Management
            <span className="text-sm font-normal bg-primary/10 text-primary px-2.5 py-1 rounded-full">
              {totalItems} {totalItems === 1 ? 'Reseller' : 'Resellers'}
            </span>
          </h1>
          <p className="text-slate-500">Manage all reseller stores, settings, and accounts</p>
        </div>
        <div className="flex gap-2">
          <Button onClick={fetchData} variant="outline" className="gap-2">
            <RefreshCw className="w-4 h-4" />
            Refresh
          </Button>
        </div>
      </div>

      {/* Search + filters */}
      <div className="bg-white rounded-xl shadow-sm border p-4">
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input
              placeholder="Search by name, subdomain, or email..."
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                setPage(1);
              }}
              className="pl-10 focus:ring-primary focus:border-primary"
            />
          </div>
          <Select
            value={filterModule}
            onValueChange={(v) => { setFilterModule(v); setPage(1); }}
          >
            <SelectTrigger className="w-full sm:w-52"><SelectValue placeholder="Platform" /></SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL}>All platforms</SelectItem>
              {MODULES.map((name) => (
                <SelectItem key={name} value={name}>{MODULE_LABELS[name]}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select
            value={filterStatus}
            onValueChange={(v) => { setFilterStatus(v); setPage(1); }}
          >
            <SelectTrigger className="w-full sm:w-40"><SelectValue placeholder="Status" /></SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL}>All statuses</SelectItem>
              <SelectItem value="active">Live</SelectItem>
              <SelectItem value="blocked">Blocked</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Resellers Table */}
      <div className="bg-white rounded-xl shadow-sm border overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow className="bg-slate-50">
              <TableHead className="w-16"></TableHead>
              <TableHead className="font-semibold">Reseller</TableHead>
              <TableHead className="font-semibold">Contact</TableHead>
              <TableHead className="font-semibold">Pricing</TableHead>
              <TableHead className="font-semibold">Platforms</TableHead>
              <TableHead className="font-semibold">Terms</TableHead>
              <TableHead className="font-semibold">Status</TableHead>
              <TableHead className="font-semibold text-center">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={8} className="text-center py-12">
                  <Loader2 className="w-10 h-10 text-primary animate-spin mx-auto mb-4" />
                  <p className="text-slate-500">Loading resellers...</p>
                </TableCell>
              </TableRow>
            ) : filteredStores.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8} className="text-center py-12">
                  <div className="bg-slate-50 rounded-2xl border-2 border-dashed p-8 max-w-sm mx-auto">
                    <Store className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                    <h3 className="text-lg font-medium text-slate-900">No resellers found</h3>
                    <p className="text-slate-500">Try adjusting your search or filters</p>
                  </div>
                </TableCell>
              </TableRow>
            ) : (
              filteredStores.map((store) => {
                const bi = store.business_info || {};
                const rs = store.reseller_settings || {};
                // Contact info can be in business_info or directly on store
                const contactEmail = bi.contact_email || store.contact_email;
                const contactPhone = bi.contact_phone || store.contact_phone;
                const logoUrl = bi.logo_url || store.logo_url;
                const moduleStates = resolveModuleStates(store);
                const terms = store.terms_acceptance || {};

                return (
                  <TableRow key={store.store_id} className="hover:bg-slate-50">
                    <TableCell>
                      {logoUrl ? (
                        <img src={getImageUrl(logoUrl)} alt={store.business_name} className="w-10 h-10 rounded-lg object-cover" />
                      ) : (
                        <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
                          <Store className="w-5 h-5 text-primary" />
                        </div>
                      )}
                    </TableCell>
                    <TableCell>
                      <div>
                        <div className="flex items-center gap-2">
                          <p className="font-medium text-slate-900">{store.business_name}</p>
                          {store.business_website && (
                            <a 
                              href={store.business_website.startsWith('http') ? store.business_website : `https://${store.business_website}`} 
                              target="_blank" 
                              rel="noopener noreferrer"
                              className="text-primary hover:text-primary/70 transition-colors"
                            >
                              <Store className="w-3.5 h-3.5" title="Visit Business Website" />
                            </a>
                          )}
                        </div>
                        <p className="text-xs text-slate-500 truncate max-w-xs">{store.subdomain}</p>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="text-sm">
                        <p className="text-slate-700">{contactEmail || <span className="text-slate-400">No email</span>}</p>
                        <p className="text-slate-500">{contactPhone || <span className="text-slate-400">No phone</span>}</p>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="text-sm">
                        <p className="font-mono font-medium text-slate-900">{store.global_price_multiplier || 2.5}x</p>
                        <p className="text-xs text-slate-500">Max: {rs.max_price_multiplier || 3.0}x</p>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex flex-col gap-1">
                        {MODULES.map((name) => {
                          const s = moduleStates[name];
                          return (
                            <Badge
                              key={name}
                              className={cn(
                                // Coloured by PLATFORM (same palette as the Channel
                                // column on /admin/orders); on/off is shown by dimming
                                // plus the suffix, so the hue never changes meaning.
                                "text-[10px] font-medium justify-start border-none shadow-none",
                                MODULE_BADGE[name],
                                s.enabled ? "" : "opacity-50"
                              )}
                            >
                              {MODULE_LABELS[name]}
                              {s.enabled ? '' : s.everEnabled ? ' · off' : ' · never'}
                            </Badge>
                          );
                        })}
                      </div>
                    </TableCell>
                    <TableCell>
                      {terms.accepted ? (
                        <div className="text-sm">
                          <Badge className="bg-emerald-100 text-emerald-700 text-[10px]">Accepted</Badge>
                          <p className="text-xs text-slate-500 mt-1">{formatDate(terms.accepted_at)}</p>
                        </div>
                      ) : moduleStates.infinity_touch.enabled ? (
                        <Badge className="bg-amber-100 text-amber-700 text-[10px]">Pending</Badge>
                      ) : (
                        // The terms gate only applies to funeral homes on Infinity Touch.
                        <span className="text-xs text-slate-400">Not required</span>
                      )}
                    </TableCell>
                    <TableCell>
                      <div className="flex flex-col gap-1">
                        <Badge className={store.is_active ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}>
                          {store.is_active ? 'Active' : 'Blocked'}
                        </Badge>
                      </div>
                    </TableCell>
                    <TableCell className="text-center">
                      <div className="flex gap-2 justify-center">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => openEditPanel(store)}
                        >
                          <Pencil className="w-4 h-4 mr-1" />
                          Edit
                        </Button>
                        {/* The colour-coded label is back alongside the switch: a bare
                            switch made the live/blocked state easy to misread, and the
                            tooltip that replaced the label only shows on hover. */}
                        <div className="flex items-center gap-2 px-3 py-1.5 bg-slate-50 rounded-lg border border-slate-100 hover:bg-slate-100/80 transition-colors">
                          <span className={cn(
                            "text-[10px] font-bold uppercase tracking-wider",
                            store.is_active !== false ? "text-green-600" : "text-red-500"
                          )}>
                            {store.is_active !== false ? 'Live' : 'Blocked'}
                          </span>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Switch
                                checked={store.is_active !== false}
                                onCheckedChange={() => toggleBlock(store)}
                                className={STATUS_SWITCH}
                              />
                            </TooltipTrigger>
                            <TooltipContent>
                              Toggle status: {store.is_active !== false ? 'Live' : 'Blocked'}
                            </TooltipContent>
                          </Tooltip>
                        </div>
                        <a
                          href={getStorePreviewUrl(store.subdomain)}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          <Button variant="ghost" size="sm">
                            <Eye className="w-4 h-4" />
                          </Button>
                        </a>
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </div>

      {/* Pagination Controls */}
      {totalItems > 0 && (
        <div className="flex flex-col sm:flex-row items-center justify-between gap-4 py-4 px-6 bg-slate-50 border-t rounded-b-xl">
          <div className="flex items-center gap-4 text-sm text-slate-500">
            <div>
              Showing <span className="font-medium">{(page - 1) * limit + 1}</span> to <span className="font-medium">{Math.min(page * limit, totalItems)}</span> of <span className="font-medium">{totalItems}</span> results
            </div>
            <div className="flex items-center gap-2">
              <span className="text-slate-600">Rows per page:</span>
              <Select
                value={limit.toString()}
                onValueChange={(val) => {
                  setLimit(Number(val));
                  setPage(1);
                }}
              >
                <SelectTrigger className="h-8 w-[70px]">
                  <SelectValue placeholder={limit.toString()} />
                </SelectTrigger>
                <SelectContent side="top">
                  {[10, 15, 25, 50, 100].map((pageSize) => (
                    <SelectItem key={pageSize} value={pageSize.toString()}>
                      {pageSize}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="flex items-center gap-1">
            <Button
              variant="outline"
              size="sm"
              className="h-8 px-2"
              onClick={() => setPage(p => Math.max(1, p - 1))}
              disabled={page === 1}
            >
              Previous
            </Button>

            <div className="flex items-center mx-2 space-x-1">
              {Array.from({ length: totalPages }).map((_, i) => {
                const pageNum = i + 1;
                if (
                  pageNum === 1 ||
                  pageNum === totalPages ||
                  (pageNum >= page - 1 && pageNum <= page + 1)
                ) {
                  return (
                    <Button
                      key={pageNum}
                      variant={page === pageNum ? "default" : "outline"}
                      size="sm"
                      className={`h-8 w-8 p-0 ${page === pageNum ? 'bg-primary hover:bg-primary/90 text-white' : 'text-slate-600 hover:bg-slate-100'}`}
                      onClick={() => setPage(pageNum)}
                    >
                      {pageNum}
                    </Button>
                  );
                }

                if (pageNum === page - 2 || pageNum === page + 2) {
                  return <span key={pageNum} className="text-slate-400 px-1">...</span>;
                }

                return null;
              })}
            </div>

            <Button
              variant="outline"
              size="sm"
              className="h-8 px-2"
              onClick={() => setPage(p => Math.min(totalPages, p + 1))}
              disabled={page >= totalPages}
            >
              Next
            </Button>
          </div>
        </div>
      )}

      {/* Edit Panel (Slide-out) */}
      <Sheet open={panelOpen} onOpenChange={setPanelOpen}>
        <SheetContent className="w-full sm:max-w-lg overflow-y-auto">
          <SheetHeader className="pb-4 border-b">
            <SheetTitle className="flex items-center gap-3">
              {selectedStore?.business_info?.logo_url ? (
                <img
                  src={selectedStore.business_info.logo_url}
                  alt={selectedStore?.business_name}
                  className="w-10 h-10 rounded-lg object-cover"
                />
              ) : (
                <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
                  <Store className="w-5 h-5 text-primary" />
                </div>
              )}
              <div>
                <p className="text-lg">{selectedStore?.business_name}</p>
                <p className="text-sm font-normal text-slate-500 truncate max-w-sm">{selectedStore?.subdomain}</p>
              </div>
            </SheetTitle>
          </SheetHeader>

          <Tabs value={activeTab} onValueChange={setActiveTab} className="mt-6">
            <TabsList className="grid grid-cols-4 w-full">
              <TabsTrigger value="info" className="text-xs">
                <Building className="w-4 h-4 mr-1 hidden sm:inline" />
                Info
              </TabsTrigger>
              <TabsTrigger value="pricing" className="text-xs">
                <DollarSign className="w-4 h-4 mr-1 hidden sm:inline" />
                Pricing
              </TabsTrigger>
              <TabsTrigger value="theme" className="text-xs">
                <Palette className="w-4 h-4 mr-1 hidden sm:inline" />
                Theme
              </TabsTrigger>
              <TabsTrigger value="account" className="text-xs">
                <User className="w-4 h-4 mr-1 hidden sm:inline" />
                Account
              </TabsTrigger>
            </TabsList>

            {/* Business Info Tab */}
            <TabsContent value="info" className="space-y-4 mt-4">
              {/* Platform enablement. Saves immediately on toggle rather than
                  waiting for the Info tab's Save — it is a different kind of
                  action from editing a text field, and it has to be obvious
                  that it took effect. */}
              <div className="rounded-xl border border-slate-200 p-4 space-y-3">
                <div>
                  <Label className="text-sm font-semibold">Platforms</Label>
                  <p className="text-xs text-slate-500 mt-0.5">
                    Existing orders are never affected. Turning a platform off leaves its
                    pages readable but read-only, and takes the storefront offline for
                    e-Displayroom.
                  </p>
                </div>
                {MODULES.map((name) => {
                  const state = selectedModuleStates[name];
                  return (
                    <div key={name} className="flex items-center justify-between gap-3">
                      <div>
                        <p className="text-sm font-medium text-slate-800">{MODULE_LABELS[name]}</p>
                        <p className="text-xs text-slate-500">
                          {state.enabled
                            ? 'On'
                            : state.everEnabled
                              ? 'Off — history stays visible, read-only'
                              : 'Never enabled — no menus shown'}
                        </p>
                      </div>
                      <Switch
                        checked={state.enabled}
                        disabled={savingModules}
                        onCheckedChange={(checked) => toggleModule(name, checked)}
                        className="data-[state=checked]:bg-emerald-500"
                      />
                    </div>
                  );
                })}
              </div>

              {/* Terms & conditions acceptance — read only. */}
              <div className="rounded-xl border border-slate-200 p-4">
                <Label className="text-sm font-semibold">Infinity Touch terms</Label>
                {selectedTerms.accepted ? (
                  <p className="text-xs text-slate-600 mt-1">
                    Accepted {formatDate(selectedTerms.accepted_at)}
                    {selectedTerms.accepted_by_user_id ? ` by ${selectedTerms.accepted_by_user_id}` : ''}
                    {selectedTerms.accepted_version ? ` (version ${selectedTerms.accepted_version})` : ''}
                    {selectedTerms.accepted_via ? ` via ${selectedTerms.accepted_via.replace('_', ' ')}` : ''}
                  </p>
                ) : selectedModuleStates.infinity_touch.enabled ? (
                  <p className="text-xs text-amber-600 mt-1">
                    Not yet accepted — this funeral home is prompted on next sign-in and
                    cannot use the app or panel until it accepts.
                  </p>
                ) : (
                  <p className="text-xs text-slate-500 mt-1">
                    Not required — acceptance only applies to funeral homes on Infinity Touch.
                  </p>
                )}
              </div>

              <div className="space-y-2">
                <Label>Business Name</Label>
                <Input
                  value={formData.business_name}
                  onChange={(e) => setFormData(prev => ({ ...prev, business_name: e.target.value }))}
                  placeholder="Store name"
                />
              </div>

              <div className="space-y-2">
                <Label>Store URL (Subdomain)</Label>
                <Input
                  value={formData.subdomain}
                  onChange={(e) => setFormData(prev => ({ ...prev, subdomain: e.target.value }))}
                  placeholder="store-slug (e.g. google)"
                />
              </div>

                <div className="space-y-2">
                  <Label>Store Logo</Label>
                  <div className="flex gap-2 items-center">
                    {formData.logo_url && (
                      <img src={getImageUrl(formData.logo_url)} alt="Logo" className="w-12 h-12 rounded-lg object-cover bg-slate-50 border" />
                    )}
                    <Input
                      value={formData.logo_url}
                      onChange={(e) => setFormData(prev => ({ ...prev, logo_url: e.target.value }))}
                      placeholder="Logo URL"
                      className="flex-1"
                    />
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="image/*"
                      className="hidden"
                      onChange={(e) => handleLogoUpload(e.target.files?.[0])}
                    />
                    <Button
                      variant="outline"
                      size="icon"
                      onClick={() => fileInputRef.current?.click()}
                      disabled={uploading}
                    >
                      {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                    </Button>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="flex items-center gap-1">
                      <User className="w-3 h-3" /> Contact Name
                    </Label>
                    <Input
                      value={formData.contact_name}
                      onChange={(e) => setFormData(prev => ({ ...prev, contact_name: e.target.value }))}
                      placeholder="Main contact person"
                    />
                  </div>
                <div className="space-y-2">
                  <Label>Business Website</Label>
                  <Input
                    value={formData.business_website}
                    onChange={(e) => setFormData(prev => ({ ...prev, business_website: e.target.value, social_links: { ...prev.social_links, website: e.target.value } }))}
                    placeholder="https://www.business.com"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="flex items-center gap-1">
                    <Mail className="w-3 h-3" /> Contact Email
                  </Label>
                  <Input
                    value={formData.contact_email}
                    onChange={(e) => setFormData(prev => ({ ...prev, contact_email: e.target.value }))}
                    placeholder="contact@store.com"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="flex items-center gap-1">
                    <Phone className="w-3 h-3" /> Contact Phone
                  </Label>
                  <Input
                    value={formData.contact_phone}
                    onChange={(e) => setFormData(prev => ({ ...prev, contact_phone: e.target.value }))}
                    placeholder="(555) 123-4567"
                  />
                </div>
              </div>

              <div className="flex items-center justify-between p-3 bg-slate-50 rounded-xl border border-slate-100">
                <div className="flex flex-col">
                  <Label className="text-sm font-medium leading-none cursor-pointer" htmlFor="view_show_business_name">
                    Display Business Name
                  </Label>
                  <span className="text-[10px] text-slate-500">Show name in store header and footer</span>
                </div>
                <Switch
                  id="view_show_business_name"
                  checked={formData.show_business_name}
                  onCheckedChange={(checked) => setFormData(prev => ({ ...prev, show_business_name: checked }))}
                />
              </div>

              {/* Redesigned Address Section */}
              <div className="space-y-4 pt-4 border-t">
                <h4 className="text-sm font-semibold text-slate-900 flex items-center gap-2">
                  <Building className="w-4 h-4 text-slate-400" />
                  Business Address
                </h4>
                
                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="address">Street Address</Label>
                    <Input
                      id="address"
                      value={formData.business_address.address}
                      onChange={(e) => setFormData(prev => ({ 
                        ...prev, 
                        business_address: { ...prev.business_address, address: e.target.value },
                        address: e.target.value
                      }))}
                      placeholder="Street address"
                    />
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="city">City</Label>
                      <Input
                        id="city"
                        value={formData.business_address.city}
                        onChange={(e) => setFormData(prev => ({ 
                          ...prev, 
                          business_address: { ...prev.business_address, city: e.target.value } 
                        }))}
                        placeholder="City"
                      />
                    </div>
                    <div className="space-y-2">
                       <Label>Zipcode</Label>
                       <Input
                        value={formData.business_address.zipcode}
                        onChange={(e) => setFormData(prev => ({ 
                          ...prev, 
                          business_address: { ...prev.business_address, zipcode: e.target.value } 
                        }))}
                        placeholder="Zipcode"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Country</Label>
                      <Select
                        value={formData.business_address.country}
                        onValueChange={(val) => {
                          setFormData(prev => ({ 
                            ...prev, 
                            business_address: { ...prev.business_address, country: val, state: "" } 
                          }));
                        }}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {COUNTRIES.map(c => (
                              <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label>State / Province</Label>
                      <Popover open={stateDropdownOpen} onOpenChange={setStateDropdownOpen}>
                        <PopoverTrigger asChild>
                          <Button
                            variant="outline"
                            role="combobox"
                            className={cn(
                              "w-full justify-between font-normal",
                              !formData.business_address.state && "text-muted-foreground"
                            )}
                          >
                            {formData.business_address.state
                              ? [...US_STATES, ...CA_PROVINCES].find((s) => s.value === formData.business_address.state)?.label
                              : "Select State..."}
                            <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                          </Button>
                        </PopoverTrigger>
                        <PopoverContent className="w-[240px] p-0 shadow-2xl z-[100]" align="start">
                          <Command>
                            <CommandInput placeholder="Search state..." className="h-9" />
                            <CommandEmpty>No state found.</CommandEmpty>
                            <CommandList>
                              <CommandGroup>
                                {(formData.business_address.country === 'CA' ? CA_PROVINCES : US_STATES).map((state) => (
                                  <CommandItem
                                    key={state.value}
                                    value={state.label}
                                    onSelect={() => {
                                      setFormData(prev => ({ 
                                        ...prev, 
                                        business_address: { ...prev.business_address, state: state.value } 
                                      }));
                                      setStateDropdownOpen(false);
                                    }}
                                  >
                                    {state.label}
                                    <Check
                                      className={cn(
                                        "ml-auto h-4 w-4",
                                        formData.business_address.state === state.value ? "opacity-100" : "opacity-0"
                                      )}
                                    />
                                  </CommandItem>
                                ))}
                              </CommandGroup>
                            </CommandList>
                          </Command>
                        </PopoverContent>
                      </Popover>
                    </div>
                  </div>
                </div>
              </div>

              {/* Social Links Section */}
              <div className="space-y-4 pt-4 border-t">
                <h4 className="text-sm font-semibold text-slate-900 flex items-center gap-2">
                  <Globe className="w-4 h-4 text-slate-400" />
                  Social Links
                </h4>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="flex items-center gap-1.5 text-xs">
                      <Facebook className="w-3 h-3 text-blue-600" /> Facebook
                    </Label>
                    <Input
                      value={formData.social_links.facebook}
                      onChange={(e) => setFormData(prev => ({ ...prev, social_links: { ...prev.social_links, facebook: e.target.value } }))}
                      placeholder="URL"
                      className="h-9"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="flex items-center gap-1.5 text-xs">
                      <Instagram className="w-3 h-3 text-pink-600" /> Instagram
                    </Label>
                    <Input
                      value={formData.social_links.instagram}
                      onChange={(e) => setFormData(prev => ({ ...prev, social_links: { ...prev.social_links, instagram: e.target.value } }))}
                      placeholder="URL"
                      className="h-9"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="flex items-center gap-1.5 text-xs">
                      <Twitter className="w-3 h-3 text-sky-500" /> Twitter
                    </Label>
                    <Input
                      value={formData.social_links.twitter}
                      onChange={(e) => setFormData(prev => ({ ...prev, social_links: { ...prev.social_links, twitter: e.target.value } }))}
                      placeholder="URL"
                      className="h-9"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="flex items-center gap-1.5 text-xs">
                      <Linkedin className="w-3 h-3 text-blue-700" /> LinkedIn
                    </Label>
                    <Input
                      value={formData.social_links.linkedin}
                      onChange={(e) => setFormData(prev => ({ ...prev, social_links: { ...prev.social_links, linkedin: e.target.value } }))}
                      placeholder="URL"
                      className="h-9"
                    />
                  </div>
                </div>
              </div>

              <div className="space-y-2">
                <Label className="flex items-center gap-1">
                  <FileText className="w-3 h-3" /> Description
                </Label>
                <Textarea
                  value={formData.business_description}
                  onChange={(e) => setFormData(prev => ({ ...prev, business_description: e.target.value }))}
                  placeholder="About the business..."
                  rows={3}
                />
              </div>

              {/* Assigned Distributor */}
              <div className="space-y-2">
                <Label className="flex items-center gap-1">
                  <User className="w-3 h-3" /> Assigned Distributor
                </Label>
                <Select
                  value={formData.distributor_id || "none"}
                  onValueChange={(v) => setFormData(prev => ({ ...prev, distributor_id: v === "none" ? "" : v }))}
                >
                  <SelectTrigger data-testid="distributor-select">
                    <SelectValue placeholder="Select distributor (optional)" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">No Distributor</SelectItem>
                    {distributors.map((dist) => (
                      <SelectItem key={dist.distributor_id} value={dist.distributor_id}>
                        {dist.name} ({dist.commission_percent}% commission)
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-slate-500">
                  Distributor earns commission on orders from this reseller
                </p>
              </div>

              <div className="flex gap-3 pt-4 border-t">
                <Button variant="outline" onClick={closePanel} className="flex-1">
                  Cancel
                </Button>
                <Button onClick={saveInfo} disabled={saving} className="flex-1 bg-primary hover:bg-primary/90">
                  {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                  Save Info
                </Button>
              </div>
            </TabsContent>

            {/* Pricing Tab */}
            <TabsContent value="pricing" className="space-y-4 mt-4">
              <div className="space-y-3">
                <Label>e-Displayroom Price Multiplier</Label>
                <div className="flex items-center gap-4">
                  <Slider
                    value={[formData.global_price_multiplier]}
                    onValueChange={([v]) => setFormData(prev => ({ ...prev, global_price_multiplier: v }))}
                    min={1}
                    max={5}
                    step={0.1}
                    className="flex-1"
                  />
                  <span className="w-16 text-center font-mono text-lg font-semibold text-primary">
                    {formData.global_price_multiplier.toFixed(1)}x
                  </span>
                </div>
              </div>

              <div className="space-y-3">
                <Label>Max Allowed Multiplier</Label>
                <div className="flex items-center gap-4">
                  <Slider
                    value={[formData.max_price_multiplier]}
                    onValueChange={([v]) => setFormData(prev => ({ ...prev, max_price_multiplier: v }))}
                    min={1}
                    max={5}
                    step={0.1}
                    className="flex-1"
                  />
                  <span className="w-16 text-center font-mono text-lg">
                    {formData.max_price_multiplier.toFixed(1)}x
                  </span>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Min Order Amount ($)</Label>
                  <Input
                    type="number"
                    value={formData.min_order_amount}
                    onChange={(e) => setFormData(prev => ({ ...prev, min_order_amount: parseFloat(e.target.value) || 0 }))}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Payout Threshold ($)</Label>
                  <Input
                    type="number"
                    value={formData.payout_threshold}
                    onChange={(e) => setFormData(prev => ({ ...prev, payout_threshold: parseFloat(e.target.value) || 100 }))}
                  />
                </div>
              </div>

              {/* Commission Settings */}
              <div className="space-y-4 pt-4 border-t">
                <h4 className="text-sm font-semibold flex items-center gap-2">
                  <DollarSign className="w-4 h-4 text-slate-400" />
                  Commission & Payments
                </h4>
                
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Commission Type</Label>
                    <Select
                      value={formData.commission_type}
                      onValueChange={(v) => setFormData(prev => ({ ...prev, commission_type: v }))}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="markup">Markup</SelectItem>
                        <SelectItem value="percentage">Percentage</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  
                  {formData.commission_type === 'percentage' && (
                    <div className="space-y-2">
                      <Label>Commission Percentage (%)</Label>
                      <Input
                        type="number"
                        value={formData.commission_percentage}
                        onChange={(e) => setFormData(prev => ({ ...prev, commission_percentage: parseFloat(e.target.value) || 0 }))}
                      />
                    </div>
                  )}
                </div>

                <div className="space-y-2">
                  <Label>Stripe Account ID (Optional)</Label>
                  <Input
                    value={formData.stripe_account_id}
                    onChange={(e) => setFormData(prev => ({ ...prev, stripe_account_id: e.target.value }))}
                    placeholder="acct_xxxxxxxxxxxxxx"
                  />
                </div>
              </div>

              {/* <div className="flex items-center justify-between p-4 bg-slate-50 rounded-xl mt-4">
                <div className="space-y-0.5">
                  <Label>Auto Payout</Label>
                  <p className="text-xs text-slate-500">Process payouts automatically when threshold is met</p>
                </div>
                <Switch
                  checked={formData.auto_payout_enabled}
                  onCheckedChange={(v) => setFormData(prev => ({ ...prev, auto_payout_enabled: v }))}
                />
              </div> */}

                <div className="flex items-center justify-between p-4 bg-red-50 rounded-xl border border-red-100 mt-4">
                  <div className="space-y-0.5">
                    <Label className="text-red-900 font-bold">Block Store</Label>
                    <p className="text-xs text-red-600">Prevent customers from accessing this store</p>
                  </div>
                  {/* Inverted: checked = blocked. Red when blocked, green when live. */}
                  <Switch
                    checked={!formData.is_active}
                    onCheckedChange={(blocked) => setFormData(prev => ({ ...prev, is_active: !blocked }))}
                    className={STATUS_SWITCH_INVERTED}
                  />
                </div>

              <div className="flex gap-3 pt-4">
                <Button variant="outline" onClick={closePanel} className="flex-1">
                  Cancel
                </Button>
                <Button onClick={savePricing} disabled={saving} className="flex-1 bg-primary hover:bg-primary/90">
                  {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                  Save Settings
                </Button>
              </div>
            </TabsContent>

            {/* Theme Tab */}
            <TabsContent value="theme" className="space-y-4 mt-4">
              <div className="space-y-2">
                <Label>Theme Preset</Label>
                <Select
                  value={formData.theme_preset}
                  onValueChange={(v) => {
                    setFormData(prev => ({ 
                      ...prev, 
                      theme_preset: v,
                      custom_primary_color: "",
                      custom_accent_color: "",
                      custom_secondary_color: "",
                      custom_background_color: "",
                      custom_header_bg_color: "",
                      custom_text_color: "",
                      custom_font_family: ""
                    }));
                  }}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.entries(themePresets).map(([key, preset]) => (
                      <SelectItem key={key} value={key}>
                        <div className="flex items-center gap-2">
                          <div className="flex gap-0.5">
                            <div className="w-4 h-4 rounded" style={{ backgroundColor: preset.primary_color }}></div>
                            <div className="w-4 h-4 rounded" style={{ backgroundColor: preset.accent_color }}></div>
                          </div>
                          {preset.name}
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="p-4 bg-slate-50 rounded-xl space-y-4">
                <p className="text-sm font-medium text-slate-700">Typography & Refined Styles</p>
                
                <div className="space-y-2">
                  <Label className="text-xs">Font Family</Label>
                  <Select
                    value={(formData.custom_font_family || themePresets[formData.theme_preset]?.font_family || "Inter").split(',')[0].replace(/['"]/g, '').trim()}
                    onValueChange={(v) => setFormData(prev => ({ ...prev, custom_font_family: v }))}
                  >
                    <SelectTrigger className="h-9">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Inter">Inter (Modern Sans)</SelectItem>
                      <SelectItem value="Montserrat">Montserrat (Geometric Sans)</SelectItem>
                      <SelectItem value="Arial">Arial (Classic Sans)</SelectItem>
                      <SelectItem value="Outfit">Outfit (Premium Sans)</SelectItem>
                      <SelectItem value="Playfair Display">Playfair Display (Elegant Serif)</SelectItem>
                      <SelectItem value="Lora">Lora (Classic Serif)</SelectItem>
                      <SelectItem value="Cormorant Garamond">Cormorant Garamond (Urn Shop Standard)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="text-xs">Background Color</Label>
                    <div className="flex gap-2">
                      <input
                        type="color"
                        value={formData.custom_background_color || themePresets[formData.theme_preset]?.background_color || '#ffffff'}
                        onChange={(e) => setFormData(prev => ({ ...prev, custom_background_color: e.target.value }))}
                        className="w-9 h-9 rounded cursor-pointer border"
                      />
                      <Input
                        value={formData.custom_background_color}
                        onChange={(e) => setFormData(prev => ({ ...prev, custom_background_color: e.target.value }))}
                        placeholder="Preset"
                        className="flex-1 h-9"
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label className="text-xs">Header Text Color</Label>
                    <div className="flex gap-2">
                      <input
                        type="color"
                        value={formData.custom_text_color || themePresets[formData.theme_preset]?.text_color || '#1a202c'}
                        onChange={(e) => setFormData(prev => ({ ...prev, custom_text_color: e.target.value }))}
                        className="w-9 h-9 rounded cursor-pointer border shadow-sm"
                      />
                      <Input
                        value={formData.custom_text_color}
                        onChange={(e) => setFormData(prev => ({ ...prev, custom_text_color: e.target.value }))}
                        placeholder="Preset"
                        className="flex-1 h-9 text-xs"
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label className="text-xs">Header Background</Label>
                    <div className="flex gap-2">
                      <input
                        type="color"
                        value={formData.custom_header_bg_color || themePresets[formData.theme_preset]?.header_bg_color || themePresets[formData.theme_preset]?.primary_color || '#1a365d'}
                        onChange={(e) => setFormData(prev => ({ ...prev, custom_header_bg_color: e.target.value }))}
                        className="w-9 h-9 rounded cursor-pointer border shadow-sm"
                      />
                      <Input
                        value={formData.custom_header_bg_color}
                        onChange={(e) => setFormData(prev => ({ ...prev, custom_header_bg_color: e.target.value }))}
                        placeholder="Preset"
                        className="flex-1 h-9 text-xs"
                      />
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-3 gap-4 mt-4">
                  <div className="space-y-2">
                    <Label className="text-xs">Primary Color</Label>
                    <div className="flex gap-2">
                      <input
                        type="color"
                        value={formData.custom_primary_color || themePresets[formData.theme_preset]?.primary_color || '#1a365d'}
                        onChange={(e) => setFormData(prev => ({ ...prev, custom_primary_color: e.target.value }))}
                        className="w-9 h-9 rounded cursor-pointer border shadow-sm"
                      />
                      <Input
                        value={formData.custom_primary_color}
                        onChange={(e) => setFormData(prev => ({ ...prev, custom_primary_color: e.target.value }))}
                        placeholder="Preset"
                        className="flex-1 h-9 text-xs"
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label className="text-xs">Accent Color</Label>
                    <div className="flex gap-2">
                      <input
                        type="color"
                        value={formData.custom_accent_color || themePresets[formData.theme_preset]?.accent_color || '#d69e2e'}
                        onChange={(e) => setFormData(prev => ({ ...prev, custom_accent_color: e.target.value }))}
                        className="w-9 h-9 rounded cursor-pointer border shadow-sm"
                      />
                      <Input
                        value={formData.custom_accent_color}
                        onChange={(e) => setFormData(prev => ({ ...prev, custom_accent_color: e.target.value }))}
                        placeholder="Preset"
                        className="flex-1 h-9 text-xs"
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label className="text-xs">Secondary Color</Label>
                    <div className="flex gap-2">
                      <input
                        type="color"
                        value={formData.custom_secondary_color || themePresets[formData.theme_preset]?.secondary_color || '#4a5568'}
                        onChange={(e) => setFormData(prev => ({ ...prev, custom_secondary_color: e.target.value }))}
                        className="w-9 h-9 rounded cursor-pointer border shadow-sm"
                      />
                      <Input
                        value={formData.custom_secondary_color}
                        onChange={(e) => setFormData(prev => ({ ...prev, custom_secondary_color: e.target.value }))}
                        placeholder="Preset"
                        className="flex-1 h-9 text-xs"
                      />
                    </div>
                  </div>
                </div>
              </div>

                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setFormData(prev => ({
                    ...prev,
                    custom_primary_color: "",
                    custom_accent_color: "",
                    custom_secondary_color: "",
                    custom_background_color: "",
                    custom_text_color: "",
                    custom_font_family: "",
                    custom_header_bg_color: ""
                  }))}
                  className="text-xs hover:bg-slate-100/80"
                >
                  <X className="w-3 h-3 mr-1" /> Reset All Custom Colors
                </Button>

              {/* Theme Preview */}
              <div className="p-4 rounded-xl border flex flex-col gap-3" style={{ 
                backgroundColor: formData.custom_background_color || themePresets[formData.theme_preset]?.background_color || '#ffffff',
                fontFamily: formData.custom_font_family || themePresets[formData.theme_preset]?.font_family || 'Inter'
              }}>
                {/* Contrast helper for preview */}
                {(() => {
                  const getContrast = (hex) => {
                    if (!hex || hex === 'transparent') return 'white';
                    const r = parseInt(hex.slice(1, 3), 16);
                    const g = parseInt(hex.slice(3, 5), 16);
                    const b = parseInt(hex.slice(5, 7), 16);
                    const yiq = ((r * 299) + (g * 587) + (b * 114)) / 1000;
                    return (yiq >= 128) ? 'black' : 'white';
                  };

                  const primaryBg = formData.custom_primary_color || themePresets[formData.theme_preset]?.primary_color || '#1a365d';
                  const headerBg = formData.custom_header_bg_color || themePresets[formData.theme_preset]?.header_bg_color || primaryBg;
                  const headerText = formData.custom_text_color || themePresets[formData.theme_preset]?.text_color || (headerBg === primaryBg ? 'white' : getContrast(headerBg));
                  const accentBg = formData.custom_accent_color || themePresets[formData.theme_preset]?.accent_color || '#d69e2e';
                  const textCol = formData.custom_text_color || themePresets[formData.theme_preset]?.text_color || '#1a202c';

                  return (
                    <div className="space-y-4">
                      <div
                        className="h-12 w-full rounded-lg flex items-center px-4 shadow-sm border"
                        style={{ 
                          backgroundColor: headerBg,
                          color: headerText
                        }}
                      >
                        <span className="text-sm font-bold tracking-tight">Header Preview</span>
                      </div>
                      
                      <div className="flex items-center gap-3">
                        <div
                          className="px-5 py-2 rounded-full text-sm font-bold shadow-sm"
                          style={{ 
                            backgroundColor: primaryBg,
                            color: getContrast(primaryBg)
                          }}
                        >
                          Primary Button
                        </div>
                        <div
                          className="px-5 py-2 rounded-full text-sm font-bold shadow-sm"
                          style={{ 
                            backgroundColor: accentBg,
                            color: getContrast(accentBg)
                          }}
                        >
                          Accent Action
                        </div>
                      </div>
                      <div className="flex flex-col">
                        <p className="text-xs font-semibold" style={{ color: textCol }}>
                          Typography Preview
                        </p>
                        <p className="text-[10px] opacity-70" style={{ color: textCol }}>
                          Sample text for store preview
                        </p>
                      </div>
                    </div>
                  );
                })()}
              </div>

              <div className="flex gap-3 pt-4 border-t">
                <Button variant="outline" onClick={closePanel} className="flex-1">
                  Cancel
                </Button>
                <Button onClick={saveTheme} disabled={saving} className="flex-1 bg-primary hover:bg-primary/90">
                  {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                  Save Theme
                </Button>
              </div>
            </TabsContent>


            {/* Account Tab */}
            <TabsContent value="account" className="space-y-4 mt-4">
              {userData?.noAccount ? (
                <div className="text-center py-8">
                  <div className="p-4 bg-primary/10 rounded-xl border border-primary/20 flex items-center gap-4">
                    <div className="w-16 h-16 bg-white rounded-full flex items-center justify-center shadow-sm border-2 border-primary/30">
                      <span className="text-primary font-bold text-2xl">
                        {selectedStore?.business_name?.charAt(0) || 'S'}
                      </span>
                    </div>
                    <div>
                      <h4 className="font-bold text-slate-900">{selectedStore?.business_name}</h4>
                      <p className="text-sm text-slate-500">{selectedStore?.subdomain}.edisplayroom.com</p>
                      <span className="inline-block px-2 py-0.5 bg-primary/20 text-primary text-[10px] uppercase font-bold rounded mt-1">Store #{selectedStore?.store_id}</span>
                    </div>
                  </div>
                  <h3 className="text-lg font-semibold text-slate-900 mb-2">No Account Linked</h3>
                  <p className="text-sm text-slate-500 mb-4">
                    This store doesn't have an associated user account yet.
                  </p>
                  <p className="text-xs text-slate-400">
                    To create an account for this reseller, use the Applications page to approve a new request or create one manually.
                  </p>
                </div>
              ) : userData ? (
                <>
                  <div className="space-y-2">
                    <Label className="flex items-center gap-1">
                      <User className="w-3 h-3" /> Account Name
                    </Label>
                    <Input
                      value={formData.user_name}
                      onChange={(e) => setFormData(prev => ({ ...prev, user_name: e.target.value }))}
                      placeholder="User's name"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label className="flex items-center gap-1">
                      <Mail className="w-3 h-3" /> Login Email
                    </Label>
                    <Input
                      type="email"
                      value={formData.user_email}
                      onChange={(e) => setFormData(prev => ({ ...prev, user_email: e.target.value }))}
                      placeholder="user@example.com"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label className="flex items-center gap-1">
                      <Lock className="w-3 h-3" /> Password
                    </Label>
                    <div className="relative">
                      <Input
                        type={showPassword ? "text" : "password"}
                        value={formData.user_password}
                        onChange={(e) => setFormData(prev => ({ ...prev, user_password: e.target.value }))}
                        placeholder="••••••••"
                        className="pr-10"
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-3 top-1/2 transform -translate-y-1/2 text-slate-400 hover:text-slate-600"
                      >
                        {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                    </div>
                    <p className="text-xs text-slate-500">Leave blank to keep current password</p>
                  </div>

                  <div className="p-4 bg-blue-50 rounded-xl">
                    <p className="text-sm text-blue-700">
                      <strong>Note:</strong> This is the login credentials for the reseller to access their dashboard.
                    </p>
                  </div>

                  <div className="flex gap-3 pt-4 border-t">
                    <Button variant="outline" onClick={closePanel} className="flex-1">
                      Cancel
                    </Button>
                    <Button onClick={saveAccount} disabled={saving} className="flex-1 bg-primary hover:bg-primary/90">
                      {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                      Save Account
                    </Button>
                  </div>
                </>
              ) : (
                <div className="text-center py-8 text-slate-500">
                  <Loader2 className="w-6 h-6 animate-spin mx-auto mb-2" />
                  <p className="text-sm">Loading account data...</p>
                </div>
              )}
            </TabsContent>
          </Tabs>
        </SheetContent>
      </Sheet>

      <AlertDialog open={blockDialogOpen} onOpenChange={setBlockDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {storeToToggle?.is_active ? 'Block Store?' : 'Unblock Store?'}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {storeToToggle?.is_active 
                ? `Are you sure you want to block "${storeToToggle.business_name}"? Customers will no longer be able to access this store.`
                : `Are you sure you want to unblock "${storeToToggle?.business_name}"? This will restore public access to the store.`}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction 
              onClick={confirmToggleBlock}
              className={storeToToggle?.is_active ? "bg-red-600 hover:bg-red-700" : "bg-green-600 hover:bg-green-700"}
            >
              {storeToToggle?.is_active ? 'Confirm Block' : 'Confirm Unblock'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
    </TooltipProvider>
  );
}
